/**
 * Vue plugin to globally register all SVG icon components from a directory.
 *
 * This plugin uses Vite's `import.meta.glob` to eagerly import all `.vue` files
 * in the `@/components/icons` directory and registers each as a global Vue component.
 * The component name is determined by the component's `name` property, or by the filename
 * with "Icon" appended if no name is specified.
 *
 * @example
 * // In your main.js:
 * import { createApp } from 'vue';
 * import App from './App.vue';
 * import GlobalIcons from './plugins/global-icons';
 *
 * const app = createApp(App);
 * app.use(GlobalIcons);
 * app.mount('#app');
 *
 * @type {import('vue').Plugin}
 */
export const REGISTERED_ICON_NAMES = [];

// This is used to register the names of the icons on kebab case for the showcase
const toKebabCase = (str) => {
  if (!str) return '';
  str = str.replace(/Icon$/, '');
  str = str.replace(/([A-Z])/g, '-$1');
  return str.toLowerCase().replace(/^-/, '');
};

export default {
  /**
   * Install function called by Vue when the plugin is used.
   *
   * @param {import('vue').App} app - The Vue application instance.
   */
  install: (app) => {
    const modules = import.meta.glob('@/components/icons/*.vue', { eager: true });
    Object.entries(modules).forEach(([path, module]) => {
      // Get the component's name, fallback to filename if not defined
      const component = module.default;
      const name = component.name || path.split('/').pop().replace('.vue', '') + 'Icon';
      app.component(name, component);
      REGISTERED_ICON_NAMES.push(toKebabCase(name));
    });
  },
};
